import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboardStats } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  FileText, 
  Package, 
  FileCheck, 
  AlertTriangle,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      {/* Welcome section */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h2 className="text-2xl font-bold text-slate-900 font-heading">
          Welcome Back
        </h2>
        <p className="text-slate-600 mt-1">
          Manage your purchase orders and generate invoices efficiently.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* PO Stats */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Pending POs
            </CardTitle>
            <Clock className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {stats?.pos?.pending || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.pos?.total || 0} total POs
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Invoiced
            </CardTitle>
            <CheckCircle className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {stats?.pos?.invoiced || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Ready to print
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Total Stock
            </CardTitle>
            <Package className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {stats?.inventory?.total_stock || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.inventory?.total_items || 0} unique SKUs
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Stock Alerts
            </CardTitle>
            <AlertTriangle className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {stats?.inventory?.out_of_stock_count || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.inventory?.low_stock_count || 0} low stock items
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-600 rounded-sm flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Process POs</h3>
                <p className="text-slate-400 text-sm">Upload and manage purchase orders</p>
              </div>
            </div>
            <Link to="/po">
              <Button 
                variant="secondary" 
                className="w-full mt-4 btn-hover"
                data-testid="go-to-po-btn"
              >
                Go to PO Processing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                <Package className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-slate-900">Inventory</h3>
                <p className="text-slate-500 text-sm">Sync and manage stock levels</p>
              </div>
            </div>
            <Link to="/inventory">
              <Button 
                variant="outline" 
                className="w-full mt-4 btn-hover"
                data-testid="go-to-inventory-btn"
              >
                Manage Inventory
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-slate-900">Invoices</h3>
                <p className="text-slate-500 text-sm">View generated invoices</p>
              </div>
            </div>
            <Link to="/invoices">
              <Button 
                variant="outline" 
                className="w-full mt-4 btn-hover"
                data-testid="go-to-invoices-btn"
              >
                View Invoices
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Status overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">PO Status Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-amber-100 text-amber-900">
                <Clock className="w-3 h-3 mr-1" />
                Pending
              </Badge>
              <span className="font-mono text-lg">{stats?.pos?.pending || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-900">
                <CheckCircle className="w-3 h-3 mr-1" />
                Invoiced
              </Badge>
              <span className="font-mono text-lg">{stats?.pos?.invoiced || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-slate-300">
                <XCircle className="w-3 h-3 mr-1" />
                Closed
              </Badge>
              <span className="font-mono text-lg">{stats?.pos?.closed || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
